import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Search } from "lucide-react";

const PKR = (n) => `Rs ${Number(n || 0).toLocaleString("en-PK")}`;

const STATUS_STEPS = ["pending", "approved", "preparing", "out_for_delivery", "completed"];

export default function TrackOrder() {
  const [params] = useSearchParams();
  const initial = params.get("o") || "";
  const [num, setNum] = useState(initial);
  const [order, setOrder] = useState(null);
  const [err, setErr] = useState("");

  const search = async (e) => {
    if (e) e.preventDefault();
    setErr(""); setOrder(null);
    if (!num) return;
    try {
      const { data } = await api.get(`/orders/track/${num}`);
      setOrder(data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Order not found");
    }
  };

  useEffect(() => { if (initial) search(); /* eslint-disable-line */ }, []);

  const stepIdx = order ? STATUS_STEPS.indexOf(order.status) : -1;

  return (
    <div className="pt-24 pb-16 max-w-3xl mx-auto px-4" data-testid="track-page">
      <h1 className="font-serif text-4xl text-white mb-2">Track Your Order</h1>
      <p className="text-zinc-400 mb-6">Enter your order number to see the latest status.</p>
      <form onSubmit={search} className="flex gap-2 mb-6">
        <input data-testid="track-input" value={num} onChange={(e) => setNum(e.target.value.toUpperCase())} placeholder="e.g. MH251104ABC123" className="input-lux flex-1" />
        <button data-testid="track-submit" className="btn-gold"><Search size={16} /> Track</button>
      </form>
      {err && <div className="text-red-400 text-sm" data-testid="track-error">{err}</div>}
      {order && (
        <div className="card-lux p-5" data-testid="track-result">
          <div className="flex justify-between items-start">
            <div>
              <div className="font-mono text-amber-200">{order.order_number}</div>
              <div className="text-xs text-zinc-500">{new Date(order.created_at).toLocaleString()}</div>
            </div>
            <span className={`status-tag status-${order.status}`}>{order.status.replace(/_/g, " ")}</span>
          </div>

          {order.status !== "cancelled" && (
            <div className="mt-6 grid grid-cols-5 gap-2">
              {STATUS_STEPS.map((s, i) => (
                <div key={s} className="flex flex-col items-center">
                  <div className={`w-8 h-8 rounded-full grid place-items-center ${i <= stepIdx ? "bg-amber-500 text-black" : "bg-zinc-800 text-zinc-600"}`}>{i + 1}</div>
                  <div className={`text-[10px] mt-2 uppercase tracking-widest font-mono ${i <= stepIdx ? "text-amber-200" : "text-zinc-600"}`}>{s.replace(/_/g, " ")}</div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-6">
            <h3 className="text-sm text-zinc-400 mb-2">Items</h3>
            <ul className="space-y-1 text-sm">
              {order.items.map((i, idx) => (
                <li key={idx} className="flex justify-between text-zinc-200"><span>{i.quantity}× {i.name}{i.variant ? ` (${i.variant})` : ""}</span><span className="font-mono">{PKR(i.unit_price * i.quantity)}</span></li>
              ))}
            </ul>
            <div className="divider-gold my-3" />
            <div className="flex justify-between text-sm text-zinc-400"><span>Subtotal</span><span className="font-mono">{PKR(order.subtotal)}</span></div>
            <div className="flex justify-between text-sm text-zinc-400"><span>Delivery</span><span className="font-mono">{PKR(order.delivery_fee)}</span></div>
            <div className="flex justify-between text-base text-amber-200 mt-1"><span>Total</span><span className="font-mono">{PKR(order.total)}</span></div>
            <div className="text-xs text-zinc-500 mt-2">Payment: {order.payment_method.toUpperCase()} · {order.payment_status}</div>
          </div>
        </div>
      )}
    </div>
  );
}
